import jsPDF from "jspdf";

/**
 * Generate and download SOAP Note as PDF (Admin Panel)
 * @param {Object} soapNoteData - SOAP note data object
 * @returns {void}
 */
export const generateSOAPNotePDF = (soapNoteData) => {
  if (!soapNoteData) {
    return;
  }

  const doc = new jsPDF({
    orientation: "portrait",
    unit: "mm",
    format: "letter",
  });

  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const margin = 20;
  const maxWidth = pageWidth - 2 * margin;
  let yPosition = margin;

  // Helper function to add new page if needed
  const checkPageBreak = (requiredHeight) => {
    if (yPosition + requiredHeight > pageHeight - margin) {
      doc.addPage();
      yPosition = margin;
      return true;
    }
    return false;
  };

  // Helper function to add text with word wrap
  const addText = (text, fontSize = 10, isBold = false, spacing = 5, lineHeight = 0.45) => {
    if (!text) return;
    
    doc.setFontSize(fontSize);
    doc.setFont("helvetica", isBold ? "bold" : "normal");
    
    const lines = doc.splitTextToSize(text, maxWidth);
    checkPageBreak(lines.length * (fontSize * lineHeight) + spacing);
    
    lines.forEach((line) => {
      doc.text(line, margin, yPosition);
      yPosition += fontSize * lineHeight;
    });
    
    yPosition += spacing;
  };

  // Patient Data Section
  const patientData = soapNoteData?.patientData;
  if (patientData) {
    doc.setFontSize(12);
    doc.setFont("helvetica", "bold");
    doc.text("Patient Data", margin, yPosition);
    yPosition += 8;

    doc.setFontSize(10);
    doc.setFont("helvetica", "normal");

    const patientFields = [
      { label: "Name", value: patientData?.name },
      { label: "Age", value: patientData?.age !== null && patientData?.age !== undefined ? String(patientData.age) : null },
      { label: "Date of Birth", value: patientData?.dateOfBirth },
      { label: "Email", value: patientData?.email },
      { label: "Phone", value: patientData?.phone },
      { label: "Gender", value: patientData?.gender },
      { label: "Height", value: patientData?.height },
      { label: "Weight", value: patientData?.weight },
      { label: "Address", value: patientData?.address },
      { label: "City", value: patientData?.city },
      { label: "State", value: patientData?.state },
      { label: "Zip Code", value: patientData?.zip },
    ];

    patientFields.forEach((field) => {
      if (field.value) {
        checkPageBreak(6);
        doc.text(`${field.label}: ${field.value}`, margin, yPosition);
        yPosition += 6;
      }
    });

    yPosition += 5;
  }

  // SUBJECTIVE Section
  if (soapNoteData?.subjective) {
    checkPageBreak(10);
    addText("SUBJECTIVE:", 12, true, 3);
    addText(soapNoteData.subjective, 10, false, 8);
  }

  // OBJECTIVE Section
  if (soapNoteData?.objective) {
    checkPageBreak(10);
    addText("OBJECTIVE:", 12, true, 3);
    addText(soapNoteData.objective, 10, false, 8);
  }

  // ASSESSMENT Section
  if (soapNoteData?.assessment) {
    checkPageBreak(10);
    addText("ASSESSMENT:", 12, true, 3);
    addText(soapNoteData.assessment, 10, false, 8);
  }

  // PRESCRIPTION Section
  if (soapNoteData?.plan) {
    checkPageBreak(10);
    addText("PRESCRIPTION:", 12, true, 3);
    addText(soapNoteData.plan, 10, false, 8);
  }

  // Prescribed On Line (Bold)
  if (soapNoteData?.prescribedOnLine) {
    checkPageBreak(8);
    addText(soapNoteData.prescribedOnLine, 10, true, 5);
  }

  // Pharmacy Details Line (Bold)
  if (soapNoteData?.pharmacyDetailsLine) {
    checkPageBreak(8);
    addText(soapNoteData.pharmacyDetailsLine, 10, true, 5);
  }

  // Generate filename
  const patientName = patientData?.name?.replace(/\s+/g, "_") || "SOAP_Note";
  const fileName = `SOAP_Note_${patientName}_${new Date().toISOString().split("T")[0]}.pdf`;

  // Save PDF
  doc.save(fileName);
};

